import { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTableColumns } from '@/hooks/useTableColumns'
import { LotHoldsDialog, type HoldCategory } from '@/components/inventory/LotHoldsDialog'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTableHead, ResizableTh, RowActions } from '@/components/table'
import { useStickyColumnEdge } from '@/hooks/useStickyColumnEdge'
import { STICKY_ROW_CLASS } from '@/lib/stickyColumn'
import { cn } from '@/lib/utils'
import { materialStock } from '@/lib/materialStock'
import {
  ChevronDown,
  ChevronRight,
  Link2,
  Plus,
  Printer,
  Trash2,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useStorageZones } from '@/hooks/usePickLists'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import type { ApiLot } from '@/types/inventory'
import type { StorageCondition } from '@/types/storage'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { MASTER_DATA_COLUMNS } from '@/lib/masterDataColumns'
import { PROFILE_TYPE_COLOR, materialProfileRef } from '@/lib/inventoryProfiles'
import { actionsProfileFor, resolveMaterialActions } from '@/lib/materialActions'
import { actionIcon } from './materialActionIcons'
import { useFieldDefs, useProfiles, useRemoveLot } from '@/hooks/useInventoryMaster'
import { familyGateStatus, groupLotsByVendorLot, totalLotStock } from '@/lib/lotFamilies'
import { procedureRunHref } from '@/lib/pendingInventory'
import { SetLotLocationDialog } from '@/components/inventory/storage/SetLotLocationDialog'
import { AddLotModal } from './AddLotModal'
import { ConnectProcedureDialog } from './ConnectProcedureDialog'
import { ReorderDialog } from './ReorderDialog'
import { CreatePurchaseOrderDialog } from './purchase-orders/CreatePurchaseOrderDialog'
import { reorderActionTitle } from '@/lib/reorder'
import { LabelPreviewDialog, type LabelPreviewRequest } from '@/components/labels/LabelPreviewDialog'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { Textarea } from '@/components/ui/textarea'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import {
  locationLabelHtml,
  locationScanCode,
  lotLabelHtml,
  lotScanCode,
} from '@/lib/inventoryLabels'
import type { ApiMaterial, InventoryProfileType, MaterialStatus } from '@/types/inventory'
import { useLocationText } from '@/hooks/useStoragePlaces'

/**
 * Lots the row's print icon labels: the in-date ones, as the expanded row
 * shows by default. More than the print service takes in one go (50) and the
 * icon opens the row instead, so the person picks which to print.
 */
const ROW_PRINT_MAX = 50

/** A lot past its expiry date. Undated stock is never expired. A lot expiring today is still valid. */
function isExpired(dateStr?: string | null): boolean {
  if (!dateStr) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return new Date(dateStr).getTime() < today.getTime()
}

/**
 * Which lots the table shows. `in_date` is the default because expired stock
 * cannot be issued — showing it by default pads every count with things nobody
 * can use, and hides the number that matters.
 */
type LotFilter = 'in_date' | 'expiring' | 'expired' | 'all'

const LOT_FILTER_LABELS: Record<LotFilter, string> = {
  in_date: 'In date',
  expiring: 'Expiring in 30 days',
  expired: 'Expired',
  all: 'All lots',
}

function matchesLotFilter(dateStr: string | null | undefined, filter: LotFilter): boolean {
  if (filter === 'all') return true
  const expired = isExpired(dateStr)
  if (filter === 'expired') return expired
  if (expired) return false
  if (filter === 'in_date') return true
  // Expiring: dated, not yet expired, inside 30 days. Undated stock has no
  // clock to be near the end of, so it is excluded rather than assumed safe.
  if (!dateStr) return false
  return Math.floor((new Date(dateStr).getTime() - Date.now()) / 86400000) <= 30
}

/**
 * One quantity, in its own column.
 *
 * On hand, in use and free used to share a single Stock cell, stacked three
 * deep. That fits, but it reads as a sentence per row and lines up with
 * nothing — comparing what is free across ten lots meant reading ten little
 * paragraphs. As columns the same numbers stack down the page, which is the
 * comparison this screen exists for.
 */
function QtyCell({
  value,
  className,
  title,
}: {
  value: number
  className?: string
  title?: string
}) {
  return (
    <td className={cn('px-3 py-1.5 tabular-nums', className)} title={title}>
      {value}
    </td>
  )
}

/**
 * The lot's total quantity — everything on the shelf, including whatever is
 * already spoken for. It is NOT the same number as Available: the two differ by
 * exactly what is in use, and they read alike only while nothing is held.
 */
function QuantityCell({ quantity }: { quantity: number }) {
  return (
    <QtyCell
      value={quantity}
      className='text-gray-700'
      title='Total on the shelf, including stock already spoken for'
    />
  )
}

/**
 * What is spoken for. Zero shows as a dash rather than `0`: a column of noughts
 * reads as data, and the only rows worth stopping on here are the ones carrying
 * a number.
 */
function InUseCell({ consumed }: { consumed: number }) {
  if (consumed <= 0) return <td className='px-3 py-1.5 text-gray-300'>—</td>
  return (
    <QtyCell
      value={consumed}
      className='font-medium text-amber-700'
      title='Already used by completed steps. Consumption has already reduced the quantity on the shelf.'
    />
  )
}

/**
 * Everything spoken for — the number the Start dialog calls "Reserve N", and
 * what the ledger records as kind RESERVED. In Use is a SUBSET of it (the part
 * whose step has started), not a rival figure, so the two are never added.
 */
function ReservedCell({ reserved, onOpen }: { reserved: number; onOpen?: () => void }) {
  if (reserved <= 0) return <td className='px-3 py-1.5 text-gray-300'>—</td>
  return (
    <HoldQtyCell
      value={reserved}
      className='font-medium text-blue-700'
      title='Spoken for but NOT yet used — still on the shelf. Click for who reserved it, when, and how much.'
      onOpen={onOpen}
    />
  )
}

/**
 * A quantity that opens its own breakdown. Falls back to a plain cell when no
 * handler is given, so the material-level roll-up (which has no single lot to
 * explain) stays inert rather than looking clickable.
 */
function HoldQtyCell({
  value,
  className,
  title,
  onOpen,
}: {
  value: number
  className?: string
  title?: string
  onOpen?: () => void
}) {
  if (!onOpen) return <QtyCell value={value} className={className} title={title} />
  // Padding and alignment must match QtyCell exactly: this cell sits in a
  // column of plain quantities, and a right-aligned clickable one made the
  // numbers step out of line with Quantity and Available beside it.
  return (
    <td className='px-3 py-1.5'>
      <button
        type='button'
        onClick={onOpen}
        title={title}
        className={cn('tabular-nums underline-offset-2 hover:underline', className)}
      >
        {value}
      </button>
    </td>
  )
}

/** On hand minus in use — what a new run can still commit. */
/**
 * A lot's free quantity.
 *
 * Red whenever the lot is QUARANTINED, however much is sitting on the shelf:
 * none of it can be committed while the gate holds it, so a green figure there
 * contradicted the Status column beside it and the allocator, which drops a
 * quarantined lot from the pick list outright.
 */
function AvailableCell({
  available,
  quarantined = false,
}: {
  available: number
  quarantined?: boolean
}) {
  return (
    <QtyCell
      value={available}
      className={cn(
        'font-medium',
        available > 0 && !quarantined ? 'text-green-700' : 'text-red-600',
      )}
      title={
        quarantined
          ? 'Quarantined — none of this can be committed to a run until its paperwork clears'
          : 'Free to commit to another run'
      }
    />
  )
}

/** The expiry date itself. Its reading — Valid, Nd left, Expired — is `StatusCell`. */
function ExpiryCell({ iso }: { iso?: string | null }) {
  // The urgency now rides on the DATE rather than occupying the Status column,
  // which belongs to the receiving gate. Same fact, same row, one column fewer
  // — and it sits beside the date it is derived from, which is where someone
  // reading "2026-09-15" wants to be told it is nearly out.
  const exp = lotExpiryStatus(iso)
  return (
    <td
      className={cn('px-3 py-1.5 whitespace-nowrap tabular-nums', exp?.dateCls ?? 'text-gray-600')}
      title={exp?.text}
    >
      {iso ? iso.slice(0, 10) : <span className='text-gray-300'>—</span>}
    </td>
  )
}

/**
 * Where a receipt stands against the Inventory procedure gating it.
 *
 * Null means not gated — a receipt for an ungated material, or one made
 * before its material was connected — and reads as `Available`: nothing is
 * holding it back, which is exactly what a process step's allocator already
 * treats it as (only `gateStatus === 'quarantine'` excludes a lot from being
 * offered or reserved). A dash here used to read as "status unknown" and let
 * an operator wonder whether the lot could actually be used.
 *
 * The badge links into the run that clears it, because that is the one action
 * this cell implies and the alternative is hunting for the lot again on another
 * tab. Cleared lots, ungated lots and lots whose run has been deleted have
 * nothing to open.
 */
function LotGateCell({
  gateStatus,
  href,
}: {
  gateStatus?: string | null
  href?: string | null
}) {
  const status = gateStatus ?? 'available'
  const badge = (
    <span
      className={cn(
        'whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
        STATUS_STYLES[status as MaterialStatus] ?? 'bg-gray-100 text-gray-500',
      )}
    >
      {STATUS_LABELS[status as MaterialStatus] ?? status}
    </span>
  )
  return (
    <td className='px-3 py-1.5'>
      {href ? (
        <Link
          to={href}
          onClick={(e) => e.stopPropagation()}
          title='Open the procedure that clears this lot'
          className='rounded-full hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        >
          {badge}
        </Link>
      ) : (
        badge
      )}
    </td>
  )
}

// The expiry reading used to be its own Status column. That column now belongs
// to the receiving gate — Quarantine / In Progress / Available and nothing else
// — so the reading moved onto the date itself (see `ExpiryCell`), where it sits
// beside the value it is derived from.

/**
 * Expiry state for a lot, based on its expiry date.
 *
 * `dateCls` colours the DATE itself, which is where this reading now lives: the
 * Status column belongs to the receiving gate. Valid stock gets no colour — a
 * table where every row is tinted says nothing about the rows that matter.
 */
function lotExpiryStatus(
  dateStr?: string | null,
): { text: string; cls: string; dateCls: string } | null {
  if (!dateStr) return null
  const days = Math.floor((new Date(dateStr).getTime() - Date.now()) / 86400000)
  if (days < 0) {
    return {
      text: 'Expired',
      cls: 'bg-red-100 text-red-700',
      dateCls: 'font-medium text-red-600',
    }
  }
  if (days <= 30) {
    return {
      text: `${days}d left`,
      cls: 'bg-amber-100 text-amber-700',
      dateCls: 'font-medium text-amber-700',
    }
  }
  return { text: 'Valid', cls: 'bg-green-100 text-green-700', dateCls: 'text-gray-600' }
}

export const STATUS_STYLES: Record<MaterialStatus, string> = {
  available: 'bg-green-100 text-green-700',
  reserved: 'bg-blue-100 text-blue-700',
  in_transit: 'bg-amber-100 text-amber-700',
  quarantine: 'bg-red-100 text-red-700',
  hold: 'bg-orange-100 text-orange-700',
  retired: 'bg-gray-100 text-gray-500',
  in_process: 'bg-indigo-100 text-indigo-700',
}
export const STATUS_LABELS: Record<MaterialStatus, string> = {
  available: 'Available',
  reserved: 'Reserved',
  in_transit: 'In Transit',
  quarantine: 'Quarantine',
  hold: 'Hold',
  retired: 'Retired',
  // "In Progress" is the business's word for it. The enum value stays
  // `in_process` on both sides of the wire — renaming a Postgres enum is a
  // migration, and this is a label, not an identity.
  in_process: 'In Progress',
}

/**
 * The status badge, which reads "Out of Stock" when there is none.
 *
 * `available` is a LIFECYCLE state — cleared for use, as opposed to quarantined
 * or retired — and it is legitimately true of a material with an empty shelf.
 * But the row also carries a numeric "Available" column, so stock at zero used
 * to render `Available: 0` in red beside a green Available badge: one word
 * meaning two unrelated things in the same row.
 *
 * Only the `available` badge is overridden. Quarantine and In Progress describe
 * where a material sits in its receiving gate, which matters whether or not any
 * stock is on the shelf — hiding those behind a stock count would lose the very
 * state this exists to show.
 *
 * Keyed on ON HAND, not on the Available column: that column is net of
 * reservations, so it reads 0 for a material whose whole shelf is committed to
 * scheduled runs. That material is spoken for, not out of stock.
 */
function stockBadge(row: { status: MaterialStatus; totalQuantity?: number }): {
  label: string
  className: string
  title?: string
} {
  if (row.status === 'available' && (row.totalQuantity ?? 0) === 0) {
    return {
      label: 'Out of Stock',
      className: 'bg-gray-100 text-gray-500',
      title: 'Cleared for use, but nothing is on hand.',
    }
  }
  return { label: STATUS_LABELS[row.status], className: STATUS_STYLES[row.status] }
}

// The profile columns come from the shared column spec; identity/status/actions
// are rendered explicitly alongside the new expand / type / total-qty columns.
const PROFILE_COLUMNS = MASTER_DATA_COLUMNS.filter((c) => c.kind === 'profile')

// Per-user column layout. The expand chevron, Material ID and the actions cell
// are locked. See src/components/table/index.ts for the pattern.
const MATERIALS_TABLE_ID = 'inventory-materials'

/** Column id for one profile column. Namespaced so a profile key added to
 *  MASTER_DATA_COLUMNS can never collide with a fixed column's id. */
function profileColumnId(key: string): string {
  return `profile:${key}`
}

const MATERIALS_FIXED_COLUMNS: TableColumnDef[] = [
  { id: 'expand', header: 'Lots', locked: true },
  { id: 'materialId', header: 'Material ID', locked: true },
  { id: 'materialCode', header: 'Material Code' },
  { id: 'materialName', header: 'Material Name' },
  { id: 'productCode', header: 'Product Code' },
  { id: 'materialType', header: 'Type' },
  // Same three figures as the lot table below, so a material and its lots are
  // read in one vocabulary. Quantity is the shelf total; Available is what a new
  // run can still take; In Use is the difference between them.
  //
  // In Use is deliberately NOT `defaultHidden`, tempting as that is on a table
  // this wide: switching the only default-hidden column back ON leaves prefs
  // that are empty, which `seedDefaultHidden` reads as "never customized" and
  // re-hides it (src/lib/tableColumnPrefs.ts). Nothing else in the app sets the
  // flag, so the hole has never been hit. It ships visible instead, and anyone
  // who does not want it can hide it — that direction stores a pref and sticks.
  { id: 'totalQty', header: 'Quantity' },
  { id: 'reserved', header: 'Reserved' },
  { id: 'inUse', header: 'In Use' },
  { id: 'available', header: 'Available' },
  { id: 'status', header: 'Status' },
]

// Profiles sit between Status and Actions, as they always have.
const MATERIALS_COLUMNS: TableColumnDef[] = [
  ...MATERIALS_FIXED_COLUMNS,
  ...PROFILE_COLUMNS.map(c => ({ id: profileColumnId(c.key), header: c.label })),
  // Pinned right: the profile columns are tenant-configured, so this table gets
  // wider the more a tenant configures, and Actions — being last — was the
  // first thing pushed off-screen. Acting on a row then cost a horizontal
  // scroll every time (z8nrz7dkkv).
  { id: 'actions', header: 'Actions', locked: true, sticky: 'right' },
]

// The nested lot table inside an expanded material row — its own table, with its
// own id.
const MATERIAL_LOTS_TABLE_ID = 'inventory-material-lots'

const MATERIAL_LOTS_COLUMNS: TableColumnDef[] = [
  { id: 'select', header: 'Select', locked: true, defaultWidth: 32 },
  // Wide, because it now carries two identifiers: the vendor's lot number as a
  // family heading and our own receipt number indented beneath it.
  { id: 'lot', header: 'Lot', locked: true, defaultWidth: 320 },
  // Quantity and Available are the two numbers people read; In Use is the
  // difference between them, kept as its own column so the gap is explained
  // rather than looking like the same figure printed twice. Hide it from the
  // Lot columns picker if the two totals are all you want.
  { id: 'quantity', header: 'Quantity', defaultWidth: 88 },
  { id: 'reserved', header: 'Reserved', defaultWidth: 88 },
  { id: 'inUse', header: 'In Use', defaultWidth: 84 },
  { id: 'available', header: 'Available', defaultWidth: 96 },
  { id: 'expiry', header: 'Expiry', defaultWidth: 112 },
  { id: 'status', header: 'Status', defaultWidth: 112 },
  // No default width: it takes whatever the screen has left. Storage paths are
  // the longest thing in the row ("LN2-01/Vapor/R1/C1/A1"), so the slack is
  // worth more here than as empty space beside a lot number.
  { id: 'location', header: 'Location' },
  { id: 'actions', header: 'Actions', locked: true, defaultWidth: 80, sticky: 'right' },
]


interface MaterialsTableProps {
  rows: ApiMaterial[]
  onOpenProfile: (material: ApiMaterial, profileType: InventoryProfileType) => void
  onDelete?: (material: ApiMaterial) => void
}

export function MaterialsTable({ rows, onOpenProfile, onDelete }: MaterialsTableProps) {
  const locate = useLocationText()
  const cols = useTableColumns(MATERIALS_TABLE_ID, MATERIALS_COLUMNS)
  const lotCols = useTableColumns(MATERIAL_LOTS_TABLE_ID, MATERIAL_LOTS_COLUMNS)
  // Drives the leading edge on the pinned Actions column — drawn only while
  // there are columns hidden underneath it. The lot tables are nested inside
  // an expanded row of this one and scroll with it, so they share the state.
  const { ref: tableRef, showEdge } = useStickyColumnEdge<HTMLTableElement>()
  const [expanded, setExpanded] = useState<string | null>(null)
  /**
   * Which vendor-lot families are open, keyed `<materialId>:<familyKey>` so the
   * set stays correct without being reset when a different material is opened.
   */
  const [openFamilies, setOpenFamilies] = useState<Set<string>>(new Set())
  const [addLotFor, setAddLotFor] = useState<ApiMaterial | null>(null)
  const [connectFor, setConnectFor] = useState<ApiMaterial | null>(null)
  const [reorderFor, setReorderFor] = useState<string | null>(null)
  // Purchase Order straight from the row, without the calculation first.
  const [poFor, setPoFor] = useState<string | null>(null)
  const [lotFilter, setLotFilter] = useState<LotFilter>('in_date')
  // Which lots to print. Empty means "everything shown" — the common case is
  // labelling a whole delivery, so requiring a selection first would add a step
  // to the frequent path to serve the rare one.
  const [selectedLotIds, setSelectedLotIds] = useState<Set<string>>(new Set())
  // Clearing the selection when a different material is opened stops ticks made
  // on one material silently applying to another's print run.
  useEffect(() => setSelectedLotIds(new Set()), [expanded])
  const [labelRequest, setLabelRequest] = useState<LabelPreviewRequest | null>(null)
  /** Which lot's holds to detail, and which category was clicked. */
  const [holdsFor, setHoldsFor] = useState<{
    lotId: string
    lotNumber: string
    category: HoldCategory
  } | null>(null)
  const removeLot = useRemoveLot()
  // Deleting a lot is irreversible, so it goes through a confirm-and-sign
  // dialog rather than firing on click — the same treatment every other
  // destructive, signed act in this app gets.
  const [deleteRequest, setDeleteRequest] = useState<{
    materialId: string
    lotId: string
    lotNumber: string
  } | null>(null)
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteError, setDeleteError] = useState<string | null>(null)
  // Also read inside LotLocationCells; TanStack dedupes, so this is the cached
  // list rather than a second fetch. Needed here because a lot label prints the
  // zone CODE and the lot only carries a zoneId.
  const { data: zones = [] } = useStorageZones({ active: true })
  const zoneCodeOf = (zoneId: string | null | undefined) =>
    zones.find((z) => z.id === zoneId)?.code ?? null

  const lotLabelFor = (material: ApiMaterial, lot: ApiLot) =>
    lotLabelHtml({
      materialCode: material.materialCode,
      materialName: material.materialName,
      lotNumber: lot.lotNumber,
      expiryDate: lot.expiryDate,
      quantity: lot.quantity,
      zoneCode: zoneCodeOf(lot.zoneId),
      locationCode: lot.locationCode,
    })

  /**
   * The scan value on a lot's label — the item code, and only that.
   *
   * The location deliberately is NOT here. Two-step pick verification only means
   * something while the shelf code lives on the shelf; printing it on the box
   * would let a picker satisfy both scans without looking at the rack.
   */
  const lotCodesFor = (material: ApiMaterial, lot: ApiLot) => {
    const item = lotScanCode({ materialCode: material.materialCode, lotNumber: lot.lotNumber })
    return item ? [{ caption: 'Item', value: item }] : []
  }

  // Every built-in action is on unless switched off in the material's actions
  // set (Master Data Config → Actions) — its own set, else the Default one.
  const { data: actionProfiles = [] } = useProfiles('actions')
  const { data: actionDefs = [] } = useFieldDefs('actions')
  const { can } = usePermission()
  // Connecting means choosing a procedure, so it needs both the manage right
  // and the ability to read procedure templates — mirrors the backend's
  // `@Permissions('inventory.manage', 'procedure_templates.read')` on the
  // connect/disconnect routes.
  const canConnectProcedure = can(PERMISSIONS.inventoryManage) && can(PERMISSIONS.procedureTemplatesRead)
  // A purchase order straight from the row needs the right to raise one — the
  // same check ReorderDialog makes before handing over to Create PO.
  const canRaisePo = can(PERMISSIONS.purchaseOrdersManage)
  /**
   * A row's buttons, from its material's actions set. Delete is split out: it
   * keeps its own red button at the end, and only where the screen passes
   * `onDelete` (it decides who may delete); the switch can hide it too.
   */
  function rowActions(row: ApiMaterial) {
    const resolved = resolveMaterialActions(actionDefs, actionsProfileFor(actionProfiles, row))
    return {
      deleteEnabled: resolved.some((a) => a.key === 'delete'),
      buttons: resolved.filter(
        (a) =>
          a.key !== 'delete' &&
          (a.key !== 'connect' || canConnectProcedure) &&
          (a.key !== 'po' || canRaisePo),
      ),
    }
  }

  const rowPrintLots = (row: ApiMaterial) =>
    (row.lots ?? []).filter((l) => matchesLotFilter(l.expiryDate, 'in_date'))

  /** The print icon's tooltip, and whether there is anything to print. */
  function rowPrintTitle(row: ApiMaterial): { title: string; disabled: boolean } {
    const n = rowPrintLots(row).length
    if (n === 0) return { title: 'No lots to label', disabled: true }
    if (n > ROW_PRINT_MAX) return { title: `${n} lots: open the row and tick the ones to print`, disabled: false }
    return { title: `Print ${n} lot label${n === 1 ? '' : 's'}`, disabled: false }
  }

  function runAction(key: string, row: ApiMaterial) {
    switch (key) {
      case 'printLabel': {
        // The same labels as "Print N labels" under the row, for in-date lots.
        const lots = rowPrintLots(row)
        if (lots.length === 0) return
        if (lots.length > ROW_PRINT_MAX) return setExpanded(row.id)
        return setLabelRequest({
          title: `${row.materialCode} · ${lots.length} lot label${lots.length === 1 ? '' : 's'}`,
          labels: lots.map((l) => lotLabelFor(row, l)),
          codes: lots.flatMap((l) => lotCodesFor(row, l)),
          print: { subject: 'lot', ids: lots.map((l) => l.id) },
        })
      }
      case 'stock':
        return setExpanded(row.id) // show on-hand lots
      case 'order':
        return setAddLotFor(row) // receive a lot
      case 'connect':
        return setConnectFor(row) // connect/replace/disconnect the gating procedure
      case 'replenish':
        // Reorder: the re-order calculation, which can hand over to Create PO
        // with the quantity settled there (for users who may raise orders).
        return setReorderFor(row.id)
      case 'po':
        // Straight to a purchase order at the server's suggested quantity.
        return setPoFor(row.id)
      default:
        return setExpanded(row.id)
    }
  }

  return (
    <>
    <div className='overflow-x-auto'>
      <Table ref={tableRef}>
        <TableHeader>
          <TableRow>
            {cols.visible.map(({ def }) => (
              <ResizableTableHead
                key={def.id}
                columnId={def.id}
                columns={cols}
                stickyEdge={showEdge}
                label={def.header}
                // Two customizable tables on this screen — a material's own
                // columns and its lots' — so each menu says which it is.
                menuLabel='Material columns'
                className={def.id === 'expand' ? 'w-8' : 'whitespace-nowrap'}
                resizable={def.id !== 'expand'}
              >
                {def.id === 'expand' ? null : def.header}
              </ResizableTableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const isOpen = expanded === row.id
            const allLots = row.lots ?? []
            // Expired stock is hidden by default: it cannot be issued, and
            // leaving it in the list pads the count with things nobody can use.
            // It is never deleted, so the filter brings it back for whoever is
            // reconciling or disposing of it.
            const expiredCount = allLots.filter((l) => isExpired(l.expiryDate)).length
            const lots = allLots.filter((l) => matchesLotFilter(l.expiryDate, lotFilter))
            // Grouped AFTER filtering, so a family whose every receipt is
            // filtered out disappears rather than lingering as an empty header.
            const families = groupLotsByVendorLot(lots)
            // A selection only counts while the lot is still on screen: filter
            // to Expired after ticking three in-date lots and printing "3
            // selected" would produce labels for rows nobody can see.
            const chosen = lots.filter((l) => selectedLotIds.has(l.id))
            const toPrint = chosen.length > 0 ? chosen : lots
            const allShownSelected = lots.length > 0 && chosen.length === lots.length
            // Totalled over the lots ON SCREEN, not over the material, so the
            // summary always adds up to the rows underneath it. The sum lives
            // in lotFamilies.ts because vitest collects only `src/**/*.test.ts`
            // — arithmetic left in this .tsx would have no coverage at all.
            const shownTotals = totalLotStock(lots)
            return (
              <Fragment key={row.id}>
                {/* Required by the pinned Actions cell, which takes `bg-inherit`
                    from this row — see STICKY_ROW_CLASS. Without the opaque
                    hover the row went see-through under the pointer, which is
                    exactly when someone is reaching for the actions. */}
                <TableRow className={STICKY_ROW_CLASS}>
                  <ColumnCells
                    columns={cols}
                    stickyEdge={showEdge}
                    cells={{
                      expand: (
                        <TableCell>
                          <button
                            type='button'
                            aria-label={`${isOpen ? 'Hide' : 'Show'} lots for ${row.materialId}`}
                            aria-expanded={isOpen}
                            onClick={() => setExpanded(isOpen ? null : row.id)}
                          >
                            {isOpen ? (
                              <ChevronDown className='h-4 w-4 text-gray-400' />
                            ) : (
                              <ChevronRight className='h-4 w-4 text-gray-400' />
                            )}
                          </button>
                        </TableCell>
                      ),
                      materialId: (
                        <TableCell className='whitespace-nowrap font-mono text-xs font-semibold text-gray-900'>
                          <span className='inline-flex items-center gap-1.5'>
                            {row.materialId}
                            {/* Gated by an Inventory procedure template. Worth a
                                mark on the row because it changes what the status
                                means: each lot received against a connected
                                material spawns its own run, and that run's forms
                                move the lot's gate — not a person choosing a
                                status. */}
                            {row.procedureTemplateId && (
                              <span
                                className='inline-flex'
                                title='Connected to an Inventory procedure template — each lot received gets its own run.'
                              >
                                <Link2
                                  className='h-3 w-3 shrink-0 text-indigo-500'
                                  aria-label='Connected to an Inventory procedure template'
                                />
                              </span>
                            )}
                          </span>
                        </TableCell>
                      ),
                      materialCode: <TableCell className='whitespace-nowrap text-gray-700'>{row.materialCode}</TableCell>,
                      materialName: <TableCell className='whitespace-nowrap text-gray-700'>{row.materialName}</TableCell>,
                      productCode: (
                        <TableCell className='whitespace-nowrap'>
                          {row.primaryProductCode ? (
                            <span className='font-mono text-xs text-gray-700'>{row.primaryProductCode}</span>
                          ) : (
                            <span className='text-gray-300'>—</span>
                          )}
                          {/* Extra codes are a count, not a wall of text — the detail
                              view lists them in full. */}
                          {(row.productCodes?.length ?? 0) > 1 && (
                            <span
                              className='ml-1.5 text-[11px] text-muted-foreground'
                              title={row.productCodes?.join(', ')}
                            >
                              +{(row.productCodes?.length ?? 1) - 1}
                            </span>
                          )}
                        </TableCell>
                      ),
                      materialType: <TableCell className='whitespace-nowrap text-gray-600'>{row.materialType}</TableCell>,
                      totalQty: (
                        <TableCell
                          className='whitespace-nowrap font-semibold tabular-nums text-gray-900'
                          title='Total on the shelf, including stock already spoken for'
                        >
                          {row.totalQuantity ?? 0}
                        </TableCell>
                      ),
                      inUse: (
                        <TableCell className='whitespace-nowrap tabular-nums'>
                          {row.reservedQuantity ? (
                            <span
                              className='font-medium text-amber-700'
                              title='Spoken for by scheduled runs and in-flight process steps. Still on the shelf — deducted at the signed Start.'
                            >
                              {row.reservedQuantity}
                            </span>
                          ) : (
                            <span className='text-gray-300'>—</span>
                          )}
                        </TableCell>
                      ),
                      /* Always shown, held stock or not. A hold does not reduce
                         the shelf count, so reading Quantity alone on a fully
                         reserved material invites a second run to be planned
                         against stock that is already committed. */
                      /* Quarantined stock is broken out rather than counted in.
                         The server's `availableQuantity` subtracts HOLDS and
                         knows nothing about the gate, so 10 free beside 30
                         quarantined read as 40 — sending someone to the shelf
                         for stock the allocator will not offer and the reserve
                         endpoint refuses. See `materialStock`. */
                      available: (() => {
                        const stock = materialStock(
                          row.lots,
                          row.availableQuantity ?? row.totalQuantity ?? 0,
                        )
                        return (
                          <TableCell
                            className='whitespace-nowrap font-semibold tabular-nums'
                            title={
                              stock.quarantined
                                ? `${stock.usable} free to commit to another run. A further ${stock.quarantined} is quarantined and cannot be used until its paperwork clears — expand for the per-lot breakdown.`
                                : row.reservedQuantity
                                  ? `Free to commit to another run. ${row.reservedQuantity} of the ${row.totalQuantity ?? 0} on the shelf is held by scheduled runs and in-flight process steps — expand for the per-lot breakdown.`
                                  : 'Free to commit to another run — nothing is currently held'
                            }
                          >
                            <span className={stock.usable > 0 ? 'text-green-700' : 'text-red-600'}>
                              {stock.usable}
                            </span>
                            {stock.quarantined ? (
                              <>
                                <span className='mx-0.5 font-normal text-gray-300'>/</span>
                                <span className='text-red-600'>{stock.quarantined}</span>
                              </>
                            ) : null}
                          </TableCell>
                        )
                      })(),
                      status: (
                        <TableCell>
                          <span
                            className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${stockBadge(row).className}`}
                            title={stockBadge(row).title}
                          >
                            {stockBadge(row).label}
                          </span>
                        </TableCell>
                      ),
                      actions: (
                        <TableCell>
                          <RowActions
                            align='start'
                            className='gap-2 whitespace-nowrap'
                            reason='No actions are enabled for this material'
                          >
                            {rowActions(row).buttons.map((a) => {
                              const Icon = actionIcon(a.key)
                              // The Connect button reports state, not just an
                              // action: filled in while a procedure template is
                              // attached, outlined while none is. Every other
                              // action is stateless, so only this one changes
                              // colour.
                              const isLive = a.key === 'connect' && !!row.procedureTemplateId
                              const print = a.key === 'printLabel' ? rowPrintTitle(row) : null
                              // Reorder reports state too: red while the
                              // material needs ordering. A row without a
                              // verdict (an older backend) stays neutral.
                              const reorder = a.key === 'replenish' ? row.reorder : undefined
                              const needsOrder = !!reorder && reorder.state !== 'ok'
                              const poTitle = reorder
                                ? reorderActionTitle(reorder.state, reorder.suggestedQty)
                                : null
                              return (
                                <button
                                  key={a.key}
                                  type='button'
                                  title={
                                    isLive
                                      ? 'Connected to an Inventory procedure template — each lot received gets its own run. Click to replace or disconnect.'
                                      : (print?.title ?? poTitle ?? a.label)
                                  }
                                  aria-label={isLive ? `${a.label} (connected)` : (print?.title ?? poTitle ?? a.label)}
                                  disabled={print?.disabled}
                                  className={cn(
                                    'flex h-7 w-7 items-center justify-center rounded-md border disabled:cursor-not-allowed disabled:opacity-40',
                                    isLive
                                      ? 'border-indigo-300 bg-indigo-50 text-indigo-600 hover:border-indigo-400 hover:bg-indigo-100'
                                      : needsOrder
                                        ? 'border-red-300 bg-red-50 text-red-600 hover:border-red-400 hover:bg-red-100'
                                        : 'border-gray-200 text-gray-600 hover:border-primary hover:text-primary disabled:hover:border-gray-200 disabled:hover:text-gray-600',
                                  )}
                                  onClick={() => runAction(a.key, row)}
                                >
                                  <Icon className='h-4 w-4' />
                                </button>
                              )
                            })}
                            {onDelete && rowActions(row).deleteEnabled && (
                              <button
                                type='button'
                                title='Delete'
                                aria-label='Delete'
                                className='flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-red-500 hover:border-red-400 hover:bg-red-50'
                                onClick={() => onDelete(row)}
                              >
                                <Trash2 className='h-4 w-4' />
                              </button>
                            )}
                          </RowActions>
                        </TableCell>
                      ),
                      // One cell per profile column, keyed the same way the
                      // column ids are built above, so a profile the user hides
                      // drops out of the row with its header.
                      ...Object.fromEntries(
                        PROFILE_COLUMNS.map(c => {
                          const pt = c.profileType as InventoryProfileType
                          const assigned = materialProfileRef(row, pt)
                          return [
                            profileColumnId(c.key),
                            <TableCell key={c.key}>
                              <button
                                type='button'
                                // An assigned profile carries its category's colour,
                                // so a row can be scanned across by column without
                                // reading the headers. "Set" stays grey: it is an
                                // invitation, not a value.
                                className={`whitespace-nowrap text-sm font-medium hover:underline ${
                                  assigned ? PROFILE_TYPE_COLOR[pt].ink : 'text-gray-400'
                                }`}
                                onClick={() => onOpenProfile(row, pt)}
                              >
                                {assigned?.name ?? 'Set'}
                              </button>
                            </TableCell>,
                          ]
                        }),
                      ),
                    }}
                  />
                </TableRow>

                {isOpen && (
                  <TableRow className='hover:bg-transparent'>
                    <TableCell colSpan={cols.visible.length} className='bg-gray-50/60 p-0'>
                      <div className='px-6 py-4'>
                        <div className='mb-2 flex items-center gap-3'>
                          <Button size='sm' className='gap-1' onClick={() => setAddLotFor(row)}>
                            <Plus className='h-4 w-4' />
                            Add lot / transaction
                          </Button>
                          {/* Batch print. A shelf is labelled in one pass, not
                              one lot at a time with a dialog between each. */}
                          {lots.length > 0 && (
                            <Button
                              size='sm'
                              variant='outline'
                              className='gap-1'
                              // Prints WHAT IS SHOWN, so the filter decides the
                              // run. "Print all" on a 200-lot material meant a
                              // 200-label job from a button that gave no hint
                              // of its size.
                              title={
                                chosen.length > 0
                                  ? `Print the ${chosen.length} selected`
                                  : 'Print every lot shown. Tick rows to print only some.'
                              }
                              // The print service takes 50 labels at a time.
                              disabled={toPrint.length > ROW_PRINT_MAX}
                              onClick={() =>
                                setLabelRequest({
                                  title: `${row.materialCode} · ${toPrint.length} lot labels`,
                                  labels: toPrint.map((l) => lotLabelFor(row, l)),
                                  codes: toPrint.flatMap((l) => lotCodesFor(row, l)),
                                  print: { subject: 'lot', ids: toPrint.map((l) => l.id) },
                                })
                              }
                            >
                              <Printer className='h-4 w-4' />
                              {toPrint.length > ROW_PRINT_MAX ? (
                                `Tick up to ${ROW_PRINT_MAX} lots to print`
                              ) : (
                                <>
                                  Print {toPrint.length} label{toPrint.length === 1 ? '' : 's'}
                                  {chosen.length > 0 && ' selected'}
                                </>
                              )}
                            </Button>
                          )}
                          <select
                            value={lotFilter}
                            onChange={(e) => setLotFilter(e.target.value as LotFilter)}
                            className='h-8 rounded-md border border-input bg-background px-2 text-xs'
                            aria-label='Filter lots'
                          >
                            {(Object.keys(LOT_FILTER_LABELS) as LotFilter[]).map((key) => (
                              <option key={key} value={key}>
                                {LOT_FILTER_LABELS[key]}
                              </option>
                            ))}
                          </select>
                          {expiredCount > 0 && lotFilter === 'in_date' && (
                            <span className='text-[11px] text-amber-700'>
                              {expiredCount} expired hidden
                            </span>
                          )}
                          <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
                            Showing {lots.length} of {allLots.length}
                          </p>
                          {/* The same three figures the columns below carry, in
                              the same colours, summed over the shown lots — so
                              the header answers "how much of this have we got,
                              and how much can I take" without adding up a
                              screenful of rows by eye. */}
                          <div
                            data-testid='lot-stock-summary'
                            className='ml-auto flex items-center gap-2 text-xs tabular-nums'
                          >
                            <span
                              className='rounded-full bg-white px-2 py-0.5 font-medium text-gray-700 ring-1 ring-gray-200'
                              title='Total on the shelf across the lots shown, including stock already spoken for'
                            >
                              {shownTotals.quantity} qty
                            </span>
                            {shownTotals.inUse > 0 && (
                              <span
                                className='rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-700'
                                title='Spoken for by scheduled runs and in-flight process steps — still on the shelf, deducted at the signed Start'
                              >
                                {shownTotals.inUse} in use
                              </span>
                            )}
                            <span
                              className={cn(
                                'rounded-full px-2 py-0.5 font-medium',
                                shownTotals.available > 0
                                  ? 'bg-green-50 text-green-700'
                                  : 'bg-red-50 text-red-600',
                              )}
                              title='Free to commit to another run — quantity minus what is in use'
                            >
                              {shownTotals.available} available
                            </span>
                          </div>
                        </div>
                        {/* The lot list scrolls inside its own box rather than
                            growing the page. A material with 200 lots otherwise
                            pushes the toolbar above it — filter, and Print —
                            somewhere you have to scroll back up to reach. */}
                        {/* Sized to the SCREEN, not to the table it sits in.
                            This panel is a cell of the materials table, which
                            scrolls horizontally across every master-data
                            column — so `w-full` here meant the width of all of
                            them, stretching the lot number across the page and
                            pushing expiry and location out of sight. Pinning it
                            to the viewport width fills the screen instead, and
                            `sticky left-0` keeps it in place when the table
                            behind it is scrolled sideways. */}
                        <div className='sticky left-0 w-[calc(100vw-7rem)] max-w-[calc(100vw-7rem)] overflow-hidden rounded-lg border border-gray-200'>
                          <div className='max-h-[22rem] overflow-y-auto'>
                            <table data-testid='material-lots' className='w-full table-fixed text-sm'>
                            <thead className='sticky top-0 z-10'>
                              {/* The column set itself lives in
                                  MATERIAL_LOTS_COLUMNS, and the user's own
                                  visibility and width sit on top of it — so the
                                  header is mapped from `lotCols.visible` rather
                                  than written out here, and the body is mapped
                                  from the same array. Writing either by hand is
                                  what lets a header and its cells shear apart. */}
                              <tr className='bg-blue-50 text-left text-xs font-semibold uppercase tracking-wide text-blue-700 [&>th]:bg-blue-50'>
                                {lotCols.visible.map(({ def }) => (
                                  <ResizableTh
                                    key={def.id}
                                    columnId={def.id}
                                    stickyEdge={showEdge}
                                    columns={lotCols}
                                    label={def.header}
                                    menuLabel='Lot columns'
                                    className={def.id === 'select' ? 'w-8 px-3 py-2' : 'px-3 py-2'}
                                    resizable={def.id !== 'select' && def.id !== 'actions'}
                                  >
                                    {def.id === 'select' ? (
                                      <input
                                        type='checkbox'
                                        aria-label='Select all shown'
                                        className='h-3.5 w-3.5 rounded border-blue-300'
                                        checked={allShownSelected}
                                        onChange={(e) =>
                                          setSelectedLotIds(
                                            e.target.checked
                                              ? new Set(lots.map((l) => l.id))
                                              : new Set(),
                                          )
                                        }
                                      />
                                    ) : (
                                      def.header
                                    )}
                                  </ResizableTh>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {families.map((family) => {
                                const famKey = `${row.id}:${family.key}`
                                // A heading is earned only by a vendor lot that
                                // actually arrived more than once. A family of
                                // one has nothing to expand, and receipts with
                                // NO supplier number are unrelated lots that
                                // were never a family — heading them would
                                // group things that have nothing in common, so
                                // they list as plain rows.
                                const hasHeader =
                                  family.vendorLotNumber !== null && family.receiptCount > 1
                                const familyOpen = !hasHeader || openFamilies.has(famKey)
                                const famIds = family.receipts.map((r) => r.id)
                                const famAllSelected = famIds.every((id) => selectedLotIds.has(id))
                                return (
                                  <Fragment key={famKey}>
                                    {hasHeader && (
                                      <tr className='border-t border-gray-200 bg-white'>
                                        <ColumnCells
                                          columns={lotCols}
                                          stickyEdge={showEdge}
                                          cells={{
                                            select: (
                                              <td className='px-3 py-1.5'>
                                                <input
                                                  type='checkbox'
                                                  aria-label={`Select all receipts of ${family.vendorLotNumber ?? ''}`}
                                                  className='h-3.5 w-3.5 rounded border-gray-300'
                                                  checked={famAllSelected}
                                                  onChange={(e) =>
                                                    setSelectedLotIds((prev) => {
                                                      const next = new Set(prev)
                                                      for (const id of famIds) {
                                                        if (e.target.checked) next.add(id)
                                                        else next.delete(id)
                                                      }
                                                      return next
                                                    })
                                                  }
                                                />
                                              </td>
                                            ),
                                            lot: (
                                              <td className='px-3 py-1.5'>
                                                <button
                                                  type='button'
                                                  className='flex items-center gap-1.5 text-left'
                                                  onClick={() =>
                                                    setOpenFamilies((prev) => {
                                                      const next = new Set(prev)
                                                      if (next.has(famKey)) next.delete(famKey)
                                                      else next.add(famKey)
                                                      return next
                                                    })
                                                  }
                                                >
                                                  <ChevronRight
                                                    className={cn(
                                                      'h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform',
                                                      familyOpen && 'rotate-90',
                                                    )}
                                                  />
                                                  {/* Same plain styling a receipt's
                                                      lot number gets: they are two
                                                      identifiers of equal standing,
                                                      and dressing one up implied a
                                                      hierarchy between them. */}
                                                  <span
                                                    className='text-xs text-gray-700'
                                                    title="Vendor's lot number"
                                                  >
                                                    {family.vendorLotNumber}
                                                  </span>
                                                  <span className='text-[11px] text-gray-500'>
                                                    · {family.receiptCount} receipts
                                                  </span>
                                                </button>
                                              </td>
                                            ),
                                            quantity: <QuantityCell quantity={family.quantity} />,
                                            reserved: <ReservedCell reserved={family.reserved} />,
                                            inUse: <InUseCell consumed={family.inUse} />,
                                            available: (
                                              <AvailableCell available={family.available} />
                                            ),
                                            expiry: <ExpiryCell iso={family.earliestExpiry} />,
                                            // A family is several receipts of one
                                            // vendor lot, each with its own gate, so
                                            // the heading answers "can I use any of
                                            // this?" using the same precedence the
                                            // material row does. No link: there is
                                            // more than one run behind it.
                                            status: (
                                              <LotGateCell
                                                gateStatus={familyGateStatus(family.receipts)}
                                              />
                                            ),
                                            /* Deliberately blank. A location is a
                                               property of one receipt — the two
                                               halves of a vendor lot can sit in
                                               different freezers — so a family has
                                               no single address to state here. */
                                            location: <td className='px-3 py-1.5' />,
                                            actions: <td className='px-3 py-1.5' />,
                                          }}
                                        />
                                      </tr>
                                    )}
                                    {familyOpen &&
                                      family.receipts.map((l) => (
                                        <Fragment key={l.id}>
                                  <tr className='border-t border-gray-100 bg-white'>
                                    <ColumnCells
                                      columns={lotCols}
                                      stickyEdge={showEdge}
                                      cells={{
                                        select: (
                                          <td className='px-3 py-1.5'>
                                            <input
                                              type='checkbox'
                                              aria-label={`Select ${l.lotNumber}`}
                                              className='h-3.5 w-3.5 rounded border-gray-300'
                                              checked={selectedLotIds.has(l.id)}
                                              onChange={(e) =>
                                                setSelectedLotIds((prev) => {
                                                  const next = new Set(prev)
                                                  if (e.target.checked) next.add(l.id)
                                                  else next.delete(l.id)
                                                  return next
                                                })
                                              }
                                            />
                                          </td>
                                        ),
                                        lot: (
                                          <td
                                            className={cn(
                                              'px-3 py-1.5 text-xs text-gray-700',
                                              hasHeader && 'pl-9',
                                            )}
                                          >
                                            <span className='inline-flex items-center gap-1.5'>
                                              {l.lotNumber}
                                              {/* The gate badge lives in the Status
                                                  column, not here: one status per
                                                  row, in the column named for it. */}
                                            </span>
                                            {/* Only where no heading carries it —
                                                under one, repeating the vendor lot
                                                on every receipt is noise. */}
                                            {!hasHeader && l.vendorLotNumber && (
                                              <div
                                                className='text-[11px] text-gray-500'
                                                title="Vendor's lot number"
                                              >
                                                {l.vendorLotNumber}
                                              </div>
                                            )}
                                          </td>
                                        ),
                                        quantity: <QuantityCell quantity={l.quantity} />,
                                        reserved: (
                                          <ReservedCell
                                            reserved={l.heldQuantity ?? l.reservedQuantity ?? 0}
                                            onOpen={() =>
                                              setHoldsFor({
                                                lotId: l.id,
                                                lotNumber: l.lotNumber,
                                                category: 'all',
                                              })
                                            }
                                          />
                                        ),
                                        inUse: <InUseCell consumed={l.consumedQuantity ?? 0} />,
                                        available: (
                                          <AvailableCell
                                            available={l.availableQuantity ?? l.quantity}
                                            quarantined={l.gateStatus === 'quarantine'}
                                          />
                                        ),
                                        expiry: <ExpiryCell iso={l.expiryDate} />,
                                        status: (
                                          <LotGateCell
                                            gateStatus={l.gateStatus}
                                            href={
                                              l.gateStatus === 'available'
                                                ? null
                                                : procedureRunHref(
                                                    l.procedureId,
                                                    l.procedurePatientMrn,
                                                  )
                                            }
                                          />
                                        ),
                                        location: (
                                          <LotLocationCells
                                            lot={l}
                                            requiresCondition={row.requiresCondition ?? null}
                                            onPrintLocation={(zoneCode, zoneName) => {
                                              // The hierarchy path IS the address for
                                              // anything shelved; the legacy bin only
                                              // covers pre-cutover stock. Encoding the
                                              // path is what lets a scan at the shelf
                                              // be checked against where the system
                                              // thinks the lot is.
                                              const address = l.storageNodePath ?? l.locationCode ?? ''
                                              const code = locationScanCode(address)
                                              setLabelRequest({
                                                title: `Location ${locate(address)}`,
                                                labels: [
                                                  locationLabelHtml({
                                                    locationCode: address,
                                                    zoneCode: l.storageNodePath
                                                      ? (address.split('/')[0] ?? null)
                                                      : zoneCode,
                                                    zoneName: l.storageNodePath ? null : zoneName,
                                                  }),
                                                ],
                                                codes: code ? [{ caption: 'Location', value: code }] : [],
                                                print: { subject: 'location', ids: [l.id] },
                                              })
                                            }}
                                          />
                                        ),
                                        actions: (
                                          <td className='px-3 py-1.5 text-right'>
                                            <button
                                              type='button'
                                              className='text-gray-300 hover:text-gray-700'
                                              title='Print lot label'
                                              onClick={() =>
                                                setLabelRequest({
                                                  title: `Lot ${l.lotNumber}`,
                                                  labels: [lotLabelFor(row, l)],
                                                  codes: lotCodesFor(row, l),
                                                  print: { subject: 'lot', ids: [l.id] },
                                                })
                                              }
                                            >
                                              <Printer className='h-4 w-4' />
                                            </button>
                                            <button
                                              type='button'
                                              className='ml-2 text-gray-300 hover:text-red-500'
                                              title='Remove lot'
                                              onClick={() =>
                                                setDeleteRequest({
                                                  materialId: row.id,
                                                  lotId: l.id,
                                                  lotNumber: l.lotNumber,
                                                })
                                              }
                                            >
                                              <Trash2 className='h-4 w-4' />
                                            </button>
                                          </td>
                                        ),
                                      }}
                                    />
                                  </tr>
                                        </Fragment>
                                      ))}
                                  </Fragment>
                                )
                              })}
                              {lots.length === 0 && (
                                <tr className='bg-white'>
                                  <td colSpan={lotCols.visible.length} className='px-3 py-2 text-sm text-muted-foreground'>
                                    No lots yet.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
    </div>

      <AddLotModal
        materialId={addLotFor?.id ?? null}
        materialLabel={addLotFor ? `${addLotFor.materialId} · ${addLotFor.materialName}` : undefined}
        requiresCondition={addLotFor?.requiresCondition ?? null}
        // Lets the modal warn that a vendor lot is a repeat. This row already
        // carries the lots for its expandable detail, so the check costs no
        // request; callers without them just get no warning.
        existingLots={addLotFor?.lots ?? undefined}
        open={addLotFor !== null}
        onClose={() => setAddLotFor(null)}
      />

      <ConnectProcedureDialog
        material={connectFor}
        open={connectFor !== null}
        onClose={() => setConnectFor(null)}
      />

      <ReorderDialog materialId={reorderFor} onClose={() => setReorderFor(null)} />
      <CreatePurchaseOrderDialog materialId={poFor} onClose={() => setPoFor(null)} />

      <LabelPreviewDialog request={labelRequest} onClose={() => setLabelRequest(null)} />
      <LotHoldsDialog
        lotId={holdsFor?.lotId ?? null}
        lotNumber={holdsFor?.lotNumber ?? ''}
        category={holdsFor?.category ?? 'all'}
        onClose={() => setHoldsFor(null)}
      />

      <SignatureDialog
        open={deleteRequest !== null}
        onOpenChange={open => {
          if (!open) {
            setDeleteRequest(null)
            setDeleteReason('')
            setDeleteError(null)
          }
        }}
        title={`Delete lot ${deleteRequest?.lotNumber ?? ''}?`}
        description='This permanently removes the lot and cannot be undone. If it is shelved, the position it occupies is freed.'
        confirmLabel='Delete & Sign'
        busy={removeLot.isPending}
        error={deleteError}
        onConfirm={(signingToken) => {
          if (!deleteRequest) return
          setDeleteError(null)
          removeLot.mutate(
            {
              materialId: deleteRequest.materialId,
              lotId: deleteRequest.lotId,
              signingToken,
              signatureMeaning: 'I am deleting this lot',
              reason: deleteReason.trim() || undefined,
            },
            {
              onSuccess: () => {
                setDeleteRequest(null)
                setDeleteReason('')
              },
              onError: err =>
                setDeleteError(apiErrorMessage(err, 'Could not delete this lot.')),
            },
          )
        }}
      >
        <div className='space-y-1.5'>
          <label className='text-xs font-medium text-gray-600'>Reason (optional)</label>
          <Textarea
            value={deleteReason}
            onChange={e => setDeleteReason(e.target.value)}
            placeholder='Why is this lot being deleted?'
            rows={2}
            className='text-sm'
          />
        </div>
      </SignatureDialog>
    </>
  )
}


/**
 * Zone + bin for one lot, edited in place.
 *
 * Per-LOT rather than per-material because that is how stock actually sits: the
 * same reagent can hold one lot in the cold room and another in the freezer, and
 * a pick sheet routes someone to the lot it chose. A lot with no zone still
 * appears on a sheet, in a terminal "unassigned" group.
 */
function LotLocationCells({
  lot,
  requiresCondition,
  onPrintLocation,
}: {
  lot: ApiLot
  /** The material's required temperature — Set location offers only places kept at it. */
  requiresCondition: StorageCondition | null
  /** Opens the label preview for this lot's bin. Zone is resolved here. */
  onPrintLocation: (zoneCode: string | null, zoneName: string | null) => void
}) {
  const locate = useLocationText()
  const { data: zones = [] } = useStorageZones({ active: true })
  const { can } = usePermission()
  const canManage = can(PERMISSIONS.inventoryManage)
  const [editing, setEditing] = useState(false)

  const zone = zones.find((z) => z.id === lot.zoneId)
  // Where the lot actually is now. Stock received since put-away moved to the
  // hierarchy has a position and no legacy zone, so reading only the old pair
  // would show every recent lot as unlocated.
  const unit = lot.storageNodePath ? lot.storageNodePath.split('/')[0] : null

  return (
    <>
      <td className='px-3 py-1.5'>
        <div className='flex items-center gap-1.5'>
          <button
            type='button'
            disabled={!canManage}
            onClick={() => setEditing(true)}
            className='text-left underline-offset-2 hover:underline disabled:cursor-default disabled:no-underline'
            title={canManage ? 'Set where this lot is stored' : undefined}
          >
            {lot.storageNodePath ? (
              <span className='flex items-center gap-1.5'>
                <span className='rounded bg-emerald-50 px-1.5 py-0.5 font-mono text-[11px] font-bold text-emerald-800'>
                  {unit}
                </span>
                <span className='font-mono text-xs text-gray-700' title={locate(lot.storageNodePath)}>
                  {locate(lot.storageNodePath)}
                </span>
              </span>
            ) : zone || lot.locationCode ? (
              // Pre-cutover stock: shown so it is not lost, in amber so it
              // reads as the older, un-validatable form of an address.
              <span className='font-mono text-xs text-amber-700'>
                {zone?.code ?? '—'}
                {lot.locationCode ? ` · ${lot.locationCode}` : ''}
              </span>
            ) : (
              <span className='font-mono text-xs text-gray-500'>
                {canManage ? 'Set location' : '—'}
              </span>
            )}
          </button>
          {/* A location label needs something scannable to encode. The
              hierarchy path is that for a shelved lot; the legacy bin code is
              for anything received before the cutover. With neither there is
              nothing to print, and a button that emits a blank symbol is worse
              than no button. */}
          {(lot.storageNodePath || lot.locationCode) && (
            <button
              type='button'
              className='text-gray-300 hover:text-gray-700'
              title='Print location label'
              onClick={() => onPrintLocation(zone?.code ?? null, zone?.name ?? null)}
            >
              <Printer className='h-3.5 w-3.5' />
            </button>
          )}
        </div>

        {/* Setting a location now picks a real slot rather than typing a zone
            and bin. The typed pair could not be rendered on a slot map or
            checked against a storage condition, which is what filled the
            unresolved-location queue in the first place. */}
        <SetLotLocationDialog
          lotId={editing ? lot.id : null}
          lotNumber={lot.lotNumber}
          currentNodeId={lot.storageNodeId}
          currentPath={lot.storageNodePath}
          requiresCondition={requiresCondition}
          open={editing}
          onClose={() => setEditing(false)}
        />
      </td>
    </>
  )
}
